import Chat from './chat/Chat'
import QueueDrawer from './QueueDrawer'
import Welcome from './Welcome'
import useCurrentRoom from '../hooks/useCurrentRoom'
import useCurrentUser from '../hooks/useCurrentUser'
import useUserIsCurator from '../hooks/useUserIsCurator'
import { useAppContext, useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import { useModalStack } from '../ui/ModalStack'
import { Grid, Title, Text, ActionIcon, Stack, Group, ThemeIcon, Popover, Tooltip } from '@mantine/core'
import { Sell } from '@mui/icons-material'
import { useState } from 'react'

export default function Listen() {

    const room = useCurrentRoom()
    const user = useCurrentUser()
    const isRoomAdmin = useUserIsCurator()
    const isGlobalAdmin = user?.roles?.includes('admin')
    const { showQueueDrawer } = useAppContext()
    const dispatch = useAppDispatch()
    const { pushModal } = useModalStack()
    const [popoverOpened, setPopoverOpened] = useState(false)

    return (
        <div className="ListenPage" style={{ position: 'relative' }}>
            <Grid
                px="md"
                py="xs"
                style={{
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    overflow: 'hidden',
                    background: 'linear-gradient(300deg, rgba(236,72,153,0.2) 0%, rgba(99,102,241,0.3) 100%)'
                }}
            >
                <Grid.Col span="auto">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        {/* Title with Popover */}
                        <Popover
                            opened={popoverOpened}
                            onChange={setPopoverOpened}
                            width={300}
                            position="bottom-start"
                            withArrow
                            shadow="md"
                        >
                            <Popover.Target>
                                <Title
                                    order={4}
                                    onClick={() => setPopoverOpened((o) => !o)}
                                    style={{
                                        margin: 0,
                                        flex: '0 1 auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'left',
                                        color: '#fff',
                                        cursor: 'pointer'
                                    }}
                                >
                                    { room?.name }
                                </Title>
                            </Popover.Target>
                            <Popover.Dropdown>
                                <Stack gap="sm">
                                    { room?.topic ? (
                                        <div>
                                            <Text size="xs" fw={700} c="dimmed" mb={4}>Topic</Text>
                                            <Text size="sm">{ room.topic }</Text>
                                        </div>
                                    ) : null }
                                    { room?.spinning ? (
                                        <div>
                                            <Text size="xs" fw={700} c="dimmed" mb={4}>Now Spinning</Text>
                                            <Group gap="xs">
                                                <ThemeIcon variant="subtle" size="xs">
                                                    <Sell />
                                                </ThemeIcon>
                                                <Text size="sm" style={{ fontStyle: 'italic' }}>
                                                    { room.spinning }
                                                </Text>
                                            </Group>
                                        </div>
                                    ) : null }
                                    { !room?.topic && !room?.spinning ? (
                                        <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
                                            No additional room information
                                        </Text>
                                    ) : null }
                                </Stack>
                            </Popover.Dropdown>
                        </Popover>

                        {/* Buttons - always on the right */}
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
                            <Tooltip label="Open user guide" openDelay={1000} zIndex={2100}>
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    size="lg"
                                    onClick={() => window.open('/help.html', '_blank')}
                                    aria-label="Help"
                                >
                                    <span className="material-symbols-outlined">help</span>
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Browse and switch rooms" openDelay={1000} zIndex={2100}>
                                <ActionIcon
                                    variant="filled"
                                    color="indigo"
                                    size="lg"
                                    onClick={() => pushModal('rooms')}
                                    aria-label="Rooms"
                                    data-tour="rooms-button"
                                >
                                    <span className="material-symbols-outlined">meeting_room</span>
                                </ActionIcon>
                            </Tooltip>
                            { room && isRoomAdmin && (
                                <Tooltip label="Invite someone to this room" openDelay={1000} zIndex={2100}>
                                    <ActionIcon
                                        variant="filled"
                                        color="indigo"
                                        size="lg"
                                        onClick={() => pushModal('share', { roomId: room.id })}
                                        aria-label="Share room"
                                    >
                                        <span className="material-symbols-outlined">person_add</span>
                                    </ActionIcon>
                                </Tooltip>
                            ) }
                            { isGlobalAdmin ? (
                                <Tooltip label="Manage music library" openDelay={1000} zIndex={2100}>
                                    <ActionIcon
                                        variant="filled"
                                        color="indigo"
                                        size="lg"
                                        onClick={() => pushModal('library')}
                                        aria-label="Library"
                                    >
                                        <span className="material-symbols-outlined">library_music</span>
                                    </ActionIcon>
                                </Tooltip>
                            ) : null }
                            { isRoomAdmin ? (
                                <Tooltip label="Edit room settings" openDelay={1000} zIndex={2100}>
                                    <ActionIcon
                                        data-tour="room-settings"
                                        variant="filled"
                                        color="indigo"
                                        size="lg"
                                        onClick={() => dispatch(Actions.showRoomDetailsModal(room.id))}
                                        aria-label="Room settings"
                                    >
                                        <span className="material-symbols-outlined">settings</span>
                                    </ActionIcon>
                                </Tooltip>
                            ) : null }
                            { room && (
                                <Tooltip label="Show/hide play queue" openDelay={1000} zIndex={2100}>
                                    <ActionIcon
                                        variant="filled"
                                        color="indigo"
                                        size="lg"
                                        onClick={() => dispatch(Actions.setShowQueueDrawer(!showQueueDrawer))}
                                        aria-label="Toggle queue"
                                        data-tour="queue-button"
                                    >
                                        <span className="material-symbols-outlined">queue_music</span>
                                    </ActionIcon>
                                </Tooltip>
                            ) }
                        </div>
                    </div>
                </Grid.Col>
            </Grid>
            { room ? <Chat /> : <Welcome /> }
            <QueueDrawer
                opened={showQueueDrawer}
                onClose={() => dispatch(Actions.setShowQueueDrawer(false))}
            />
        </div>
    )
}
